/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Stack, Typography, TextField, CircularProgress, Radio, InputAdornment, Skeleton, Alert,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from '../Iconify';
import {
    exportLeadToDentally,
    fetchDentallyPatientLink,
    linkExistingDentallyPatient,
    searchDentallyPatients,
} from '../../utils/dentallyApi';
import { callApi } from '../../utils/apiCall';
import {
    journeyPatientResultStyle,
    journeyLinkedBadgeStyle,
} from '../../styles/DentallyBookingStyle';

// Robust patient array extraction — handles multiple API response shapes
const extractPatients = (res) => {
    const d = res?.data;
    if (!d) return [];
    // Dentally API shape
    if (Array.isArray(d.patients)) return d.patients;
    // Django REST shape
    if (Array.isArray(d.results)) return d.results;
    // Direct array
    if (Array.isArray(d)) return d;
    // Wrapped in data
    if (Array.isArray(d.data)) return d.data;
    return [];
};

const getPatientName = (patient) => {
    if (patient.name) return patient.name;
    return [patient.title, patient.first_name, patient.last_name]
        .filter(Boolean).join(' ') || `Patient #${patient.id}`;
};

const getPatientDetail = (patient) => {
    const parts = [];
    if (patient.email_address || patient.email) parts.push(patient.email_address || patient.email);
    if (patient.mobile_phone || patient.phone_number || patient.mobile) {
        parts.push(patient.mobile_phone || patient.phone_number || patient.mobile);
    }
    if (patient.date_of_birth) parts.push(`DOB: ${patient.date_of_birth}`);
    return parts.join(' \u2022 ');
};

const DentallyPatientMapper = ({ leadId, practiceId, onPatientMapped, candidates }) => {
    const [mode, setMode] = useState('checking'); // 'checking' | 'linked' | 'search'
    const [linkedPatientId, setLinkedPatientId] = useState(null);

    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [isLinking, setIsLinking] = useState(false);
    const [linkError, setLinkError] = useState('');
    const debounceRef = useRef(null);
    const contextGenerationRef = useRef(0);
    const searchGenerationRef = useRef(0);
    const linkGenerationRef = useRef(0);
    const exportGenerationRef = useRef(0);

    // Export state
    const [isExporting, setIsExporting] = useState(false);
    const [exportError, setExportError] = useState('');

    // Cached lead data for pre-export validation
    const [cachedLead, setCachedLead] = useState(null);

    // Track whether we already resolved to 'linked' to avoid re-checking
    const resolvedRef = useRef(false);

    // Hydrate the durable, practice-scoped patient link and lead details. Every
    // context change invalidates reads and mutations from the previous lead.
    useEffect(() => {
        contextGenerationRef.current += 1;
        searchGenerationRef.current += 1;
        linkGenerationRef.current += 1;
        exportGenerationRef.current += 1;
        const contextGeneration = contextGenerationRef.current;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        resolvedRef.current = false;
        autoSearchedRef.current = false;
        setMode('checking');
        setLinkedPatientId(null);
        setSearchQuery('');
        setSearchResults([]);
        setIsSearching(false);
        setSelectedPatient(null);
        setIsLinking(false);
        setLinkError('');
        setIsExporting(false);
        setExportError('');
        setCachedLead(null);

        if (!leadId || !practiceId) {
            setMode('search');
            return undefined;
        }

        callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/`)
            .then((response) => {
                if (contextGeneration === contextGenerationRef.current && response?.data) {
                    setCachedLead(response.data);
                }
            })
            .catch(() => {});

        fetchDentallyPatientLink(practiceId, leadId).then((resolution) => {
            if (contextGeneration !== contextGenerationRef.current) return;
            if (resolution?.outcome === 'unique' && resolution.link?.dentally_patient_id != null) {
                const existingId = String(resolution.link.dentally_patient_id);
                resolvedRef.current = true;
                setLinkedPatientId(existingId);
                setMode('linked');
                onPatientMapped(existingId);
                return;
            }
            setMode('search');
        }).catch(() => {
            if (contextGeneration === contextGenerationRef.current) setMode('search');
        });
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [leadId, practiceId]);

    // One place the search actually runs, so typing and the automatic first
    // search cannot drift apart. Generation guards keep a slow response from a
    // previous lead (or a previous keystroke) off the current screen.
    const runSearch = useCallback(async (value, searchGeneration) => {
        const contextGeneration = contextGenerationRef.current;
        const stale = () => (
            contextGeneration !== contextGenerationRef.current
            || searchGeneration !== searchGenerationRef.current
        );
        setIsSearching(true);
        try {
            const res = await searchDentallyPatients(practiceId, value.trim());
            if (stale()) return;
            setSearchResults(extractPatients(res));
        } catch {
            if (stale()) return;
            setSearchResults([]);
        } finally {
            if (!stale()) setIsSearching(false);
        }
    }, [practiceId]);

    // Debounced search
    const handleSearchChange = useCallback((e) => {
        const val = e.target.value;
        const contextGeneration = contextGenerationRef.current;
        const searchGeneration = searchGenerationRef.current + 1;
        searchGenerationRef.current = searchGeneration;
        setSearchQuery(val);
        setSelectedPatient(null);
        setLinkError('');

        if (debounceRef.current) clearTimeout(debounceRef.current);

        if (val.trim().length < 2) {
            setSearchResults([]);
            return;
        }

        debounceRef.current = setTimeout(() => {
            if (contextGeneration !== contextGenerationRef.current
                || searchGeneration !== searchGenerationRef.current) return;
            runSearch(val, searchGeneration);
        }, 500);
    }, [practiceId, runSearch]);

    // Search once, automatically, with what the CRM already knows.
    //
    // The dialog used to open on an empty box and make the operator retype a
    // name the lead record already holds — and which this component had already
    // fetched into `cachedLead`.
    //
    // Seeded with the NAME rather than phone or email, deliberately. A sandbox
    // probe recorded in apps/dentally/patient_identity.py found that of
    // date_of_birth / mobile_phone / email_address / last_name, only
    // date_of_birth actually filters: the other three silently return the
    // unfiltered first page. The free-text `query` is what genuinely narrows
    // server-side, so this sends exactly what a person would have typed.
    //
    // It never selects anything. Results are candidates to read, not an answer:
    // one shared mobile is a household, not a person, and linking the wrong
    // patient binds this lead to someone else's clinical record. The operator
    // still picks a row and presses Link, exactly as before.
    const autoSearchedRef = useRef(false);
    useEffect(() => {
        if (mode !== 'search' || autoSearchedRef.current || !cachedLead) return;
        const seed = [cachedLead.first_name, cachedLead.last_name]
            .filter(Boolean).join(' ').trim();
        // Below the backend's min_length=2 there is nothing worth asking for.
        if (seed.length < 2) return;
        autoSearchedRef.current = true;
        const searchGeneration = searchGenerationRef.current + 1;
        searchGenerationRef.current = searchGeneration;
        setSearchQuery(seed);
        runSearch(seed, searchGeneration);
    }, [mode, cachedLead, runSearch]);

    // Patient ids the backend's post-create recheck already flagged as possibly
    // this person. Only ids and signals are stored, never names, so they are
    // used to mark and order the live results rather than rendered on their own.
    const candidateIds = useMemo(
        () => new Set((candidates || []).map((c) => String(c?.dentally_patient_id ?? c?.patient_id ?? c))),
        [candidates]
    );

    // Flagged patients first; a possible match is the row most worth reading.
    const orderedResults = useMemo(() => {
        if (candidateIds.size === 0) return searchResults;
        const flagged = (patient) => candidateIds.has(String(patient?.id));
        return [...searchResults].sort((a, b) => Number(flagged(b)) - Number(flagged(a)));
    }, [searchResults, candidateIds]);

    // Link selected patient
    const handleLinkPatient = async () => {
        if (!selectedPatient) return;
        const contextGeneration = contextGenerationRef.current;
        const linkGeneration = linkGenerationRef.current + 1;
        linkGenerationRef.current = linkGeneration;
        const operationPracticeId = practiceId;
        const operationLeadId = leadId;
        setIsLinking(true);
        setLinkError('');
        try {
            const selectedPatientId = selectedPatient.id;
            const selectedId = String(selectedPatientId);
            const result = await linkExistingDentallyPatient(
                operationPracticeId,
                operationLeadId,
                selectedPatientId
            );
            if (contextGeneration !== contextGenerationRef.current
                || linkGeneration !== linkGenerationRef.current) return;
            const confirmedId = result?.dentallyPatient?.dentally_patient_id;
            if (result?.ok && confirmedId != null && String(confirmedId) === selectedId) {
                setLinkedPatientId(selectedId);
                setMode('linked');
                onPatientMapped(selectedId);
                return;
            }

            if (result?.code === 'dentally_patient_link_conflict') {
                setLinkError('This Dentally patient is already linked to another CRM lead. No link was saved.');
                return;
            }

            const message = result?.message || 'Unable to link this Dentally patient.';
            setLinkError(result?.retryable
                ? `${message} Try again.`
                : `${message} No link was saved.`);
        } catch {
            if (contextGeneration !== contextGenerationRef.current
                || linkGeneration !== linkGenerationRef.current) return;
            setLinkError('Unable to link this Dentally patient. No link was saved.');
        } finally {
            if (contextGeneration === contextGenerationRef.current
                && linkGeneration === linkGenerationRef.current) setIsLinking(false);
        }
    };

    // Pre-export field validation — Dentally requires these to create a patient
    const getMissingDentallyFields = () => {
        const lead = cachedLead;
        const missing = [];
        if (!lead?.salutation) missing.push('Title (Mr / Mrs / Ms / Miss / Dr)');
        if (!lead?.dob) missing.push('Date of birth');
        if (!lead?.lead_contact?.email) missing.push('Email address');
        if (!lead?.lead_address?.postcode) missing.push('Postcode');
        return missing;
    };

    // Export to Dentally (creates patient + links)
    const handleExportToDentally = async () => {
        const contextGeneration = contextGenerationRef.current;
        const exportGeneration = exportGenerationRef.current + 1;
        exportGenerationRef.current = exportGeneration;
        const operationPracticeId = practiceId;
        const operationLeadId = leadId;
        const isCurrentOperation = () => (
            contextGeneration === contextGenerationRef.current
            && exportGeneration === exportGenerationRef.current
            && operationPracticeId === practiceId
            && operationLeadId === leadId
        );
        setExportError('');

        // Validate required fields before attempting export
        const missing = getMissingDentallyFields();
        if (missing.length > 0) {
            setExportError(
                `Dentally requires the following fields to create a patient record. Please go back to Personal Details and add them:\n• ${missing.join('\n• ')}`
            );
            return;
        }

        setIsExporting(true);
        try {
            const res = await exportLeadToDentally(operationLeadId);
            if (!isCurrentOperation()) return;
            if (res?.ok || res?.status === 200 || res?.status === 201) {
                const patientId = String(
                    res.data?.dentally_patient_id || res.data?.patient_id || res.data?.id || ''
                );
                if (patientId) {
                    setLinkedPatientId(patientId);
                    setMode('linked');
                    onPatientMapped(patientId);
                } else {
                    setExportError('Export succeeded but no patient ID was returned. Please search for the patient manually.');
                }
            } else {
                const errMsg = res?.data?.detail
                    || res?.data?.message
                    || (Array.isArray(res?.data?.non_field_errors) && res.data.non_field_errors[0])
                    || res?.data?.error
                    || 'Failed to export patient to Dentally. Please check the patient details and try again.';
                setExportError(errMsg);
            }
        } catch {
            if (!isCurrentOperation()) return;
            setExportError('An unexpected error occurred while exporting to Dentally.');
        } finally {
            if (isCurrentOperation()) setIsExporting(false);
        }
    };

    // ── Checking state (brief, on mount) ─────────────────────────────────
    if (mode === 'checking') {
        return (
            <Box sx={{ py: 2 }}>
                <Stack direction='row' alignItems='center' spacing={1.5}>
                    <CircularProgress size={18} />
                    <Typography variant='body2' sx={{ color: 'text.secondary', fontSize: 13 }}>
                        Checking Dentally patient link...
                    </Typography>
                </Stack>
            </Box>
        );
    }

    // ── Linked state ─────────────────────────────────────────────────────
    if (mode === 'linked' && linkedPatientId) {
        return (
            <Box sx={journeyLinkedBadgeStyle}>
                <Box sx={{
                    width: 28, height: 28, borderRadius: '50%',
                    bgcolor: 'rgba(34,197,94,0.12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    <Iconify icon='mdi:check-bold' width={16} sx={{ color: 'success.main' }} />
                </Box>
                <Box>
                    <Typography variant='body2' sx={{ fontWeight: 700, fontSize: 13, color: 'success.dark' }}>
                        Linked to Dentally Patient #{linkedPatientId}
                    </Typography>
                    <Typography variant='caption' sx={{ color: 'text.secondary', fontSize: 11 }}>
                        Patient record is connected and ready
                    </Typography>
                </Box>
            </Box>
        );
    }

    // ── Search / Export state ────────────────────────────────────────────
    return (
        <Box>
            {/* Search existing patients */}
            <Typography variant='body2' sx={{ color: 'text.secondary', fontSize: 13, mb: 1.5 }}>
                Search for an existing Dentally patient or create a new one
            </Typography>
            <TextField
                fullWidth
                size='small'
                placeholder='Search by name, email, or phone...'
                value={searchQuery}
                onChange={handleSearchChange}
                sx={{
                    mb: 1.5,
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        bgcolor: 'rgba(145,158,171,0.04)',
                    },
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position='start'>
                            <Iconify icon='mdi:magnify' width={18} sx={{ color: 'text.disabled' }} />
                        </InputAdornment>
                    ),
                    endAdornment: isSearching ? (
                        <InputAdornment position='end'>
                            <CircularProgress size={16} />
                        </InputAdornment>
                    ) : null,
                }}
            />

            {/* Search results — flagged possible matches first. */}
            {orderedResults.length > 0 && (
                <Stack spacing={0.5} sx={{ mb: 1.5, maxHeight: 180, overflowY: 'auto' }}>
                    {orderedResults.map((patient) => {
                        const isSelected = selectedPatient?.id === patient.id;
                        const isCandidate = candidateIds.has(String(patient?.id));
                        const name = getPatientName(patient);
                        const detail = getPatientDetail(patient);
                        return (
                            <Box
                                key={patient.id}
                                onClick={() => setSelectedPatient(patient)}
                                sx={journeyPatientResultStyle(isSelected)}
                            >
                                <Stack direction='row' alignItems='center' spacing={1}>
                                    <Radio
                                        size='small'
                                        checked={isSelected}
                                        sx={{ p: 0, color: 'text.disabled' }}
                                    />
                                    <Box sx={{ minWidth: 0 }}>
                                        <Stack direction='row' alignItems='center' spacing={0.75} sx={{ minWidth: 0 }}>
                                            <Typography variant='body2' noWrap sx={{ fontWeight: 600, fontSize: 13 }}>
                                                {name}
                                            </Typography>
                                            {/* Worded as "possible" on purpose: the backend flagged this
                                                record, it did not confirm it. Linking the wrong patient
                                                binds the lead to someone else's clinical record. */}
                                            {isCandidate && (
                                                <Box
                                                    component='span'
                                                    sx={{
                                                        flexShrink: 0, fontSize: 9, fontWeight: 700, lineHeight: 1.6,
                                                        px: 0.75, borderRadius: 0.75, letterSpacing: '0.3px',
                                                        color: 'warning.dark', bgcolor: 'warning.lighter',
                                                    }}
                                                >
                                                    POSSIBLE MATCH
                                                </Box>
                                            )}
                                        </Stack>
                                        {detail && (
                                            <Typography variant='caption' noWrap sx={{ color: 'text.disabled', fontSize: 10 }}>
                                                #{patient.id} &bull; {detail}
                                            </Typography>
                                        )}
                                        {!detail && (
                                            <Typography variant='caption' sx={{ color: 'text.disabled', fontSize: 10 }}>
                                                #{patient.id}
                                            </Typography>
                                        )}
                                    </Box>
                                </Stack>
                            </Box>
                        );
                    })}
                </Stack>
            )}

            {/* Searching skeleton */}
            {isSearching && orderedResults.length === 0 && searchQuery.trim().length >= 2 && (
                <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                    {[0, 1, 2].map((i) => (
                        <Box key={i} sx={{ p: 1.25, borderRadius: 1.5, border: '1px solid rgba(145,158,171,0.12)' }}>
                            <Stack direction='row' alignItems='center' spacing={1}>
                                <Skeleton variant='circular' width={20} height={20} />
                                <Box sx={{ flex: 1 }}>
                                    <Skeleton variant='text' width='55%' height={16} />
                                    <Skeleton variant='text' width='35%' height={12} />
                                </Box>
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            )}

            {/* No results found */}
            {!isSearching && searchQuery.trim().length >= 2 && orderedResults.length === 0 && (
                <Typography variant='caption' sx={{ color: 'text.disabled', display: 'block', mb: 1.5, fontSize: 11 }}>
                    No matching patients found in Dentally
                </Typography>
            )}

            {/* Link button */}
            {selectedPatient && (
                <>
                    <LoadingButton
                        fullWidth
                        variant='contained'
                        size='small'
                        loading={isLinking}
                        onClick={handleLinkPatient}
                        startIcon={<Iconify icon='mdi:link-variant' />}
                        sx={{
                            mb: 1.5,
                            borderRadius: '10px',
                            fontWeight: 700,
                            textTransform: 'none',
                            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                            boxShadow: '0 2px 8px rgba(24,144,255,0.25)',
                        }}
                    >
                        Link Patient: {getPatientName(selectedPatient)}
                    </LoadingButton>
                    {linkError && (
                        <Alert severity='warning' sx={{ mb: 1.5, borderRadius: '10px', fontSize: 12 }}>
                            {linkError}
                        </Alert>
                    )}
                </>
            )}

            {/* Divider with OR */}
            <Stack direction='row' alignItems='center' spacing={1.5} sx={{ my: 1.5 }}>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145,158,171,0.16)' }} />
                <Typography variant='caption' sx={{ color: 'text.disabled', fontWeight: 700, fontSize: 10, letterSpacing: '1px' }}>
                    OR
                </Typography>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145,158,171,0.16)' }} />
            </Stack>

            {/* Export error */}
            {exportError && (
                <Alert
                    severity='error'
                    sx={{ mb: 1.5, borderRadius: '10px', fontSize: 12, whiteSpace: 'pre-line' }}
                    onClose={() => setExportError('')}
                >
                    {exportError}
                </Alert>
            )}

            {/* Export to Dentally */}
            <LoadingButton
                fullWidth
                variant='outlined'
                loading={isExporting}
                onClick={handleExportToDentally}
                startIcon={<Iconify icon='mdi:account-plus-outline' />}
                sx={{
                    borderRadius: '10px',
                    fontWeight: 700,
                    textTransform: 'none',
                    borderColor: 'rgba(145,158,171,0.3)',
                    color: 'text.primary',
                    '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'rgba(24,144,255,0.04)',
                    },
                }}
            >
                Export Lead To Dentally (Create New Patient)
            </LoadingButton>
        </Box>
    );
};

DentallyPatientMapper.propTypes = {
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onPatientMapped: PropTypes.func.isRequired,
    // From the lead's stored `dentally_possible_match`. Ids and signals only.
    candidates: PropTypes.arrayOf(PropTypes.object),
};

DentallyPatientMapper.defaultProps = {
    candidates: [],
};

export default DentallyPatientMapper;
